import requester from '@/lib/axios';
import JsonApiResponse from '@/models/JsonApiResponse';
import { Project, ProjectMetadata } from '@/models/Project';
import { BaseFilters, ListServiceFunc } from '@/models/Service';
import { ProjectCourseFlagEnum } from '@/models/Flag';
import { FlagTypeEnum } from '@/models/Flag';
import { convertToFormData } from '@/utils/convertToFormData';
import { MULTIPART_FORM_DATA_HEADERS } from '@/constants';
import { isUndefined, omitBy } from 'lodash';

export type ProjectFilters = BaseFilters & {
  byUpVotes?: 'more' | 'less';
  byViews?: 'more' | 'less';
  courseAbbreviation?: string;
  klass?: number;
  toolsId?: number[];
  user?: number;
  unitId?: number[];
  flagsId?: number[];
  flagsIdNot?: number[];
  flagsType?: FlagTypeEnum[];
  courseType?: ProjectCourseFlagEnum[];
  dateAfter?: string;
  dateBefore?: string;
  shunfle?: boolean;
};

export const listProjects: ListServiceFunc<
  Project,
  ProjectFilters
> = async params => {
  const { data } = await requester().get<JsonApiResponse<Project>>(
    `projects/`,
    {
      params,
    },
  );
  return data;
};

export const getProject = async (id: number) => {
  const { data: project } = await requester().get<Project>(`projects/${id}/`);
  return project;
};

export const deleteProject = async (id: number) => {
  await requester().delete(`projects/${id}/`);
};

export type ProjectDTO = Pick<
  Project,
  | 'projectUrl'
  | 'title'
  | 'description'
  | 'shared'
  | 'equipName'
  | 'teacherMentorId'
> & {
  image?: File;
  imageUrl?: string;
  toolId: number;
  usersIds: number[];
  flagsIds?: number[];
  unitsIds?: number[];
  documentation?: File | string;
  video?: File | string;
};

export const createProject = async (project: ProjectDTO) => {
  const body = omitBy(
    { ...project, imageUrl: project.imageUrl || '' },
    isUndefined,
  );
  const formData = convertToFormData(body);

  const { data: createdProject } = await requester().post<Project>(
    'projects/',
    formData,
    { headers: MULTIPART_FORM_DATA_HEADERS },
  );
  return createdProject;
};

export const updateProject = async (
  id: number,
  { image, documentation, video, ...rest }: Partial<ProjectDTO>,
) => {
  const { data: projectWithoutFiles } = await requester().patch<Project>(
    `projects/${id}/`,
    {
      ...rest,
      documentation: documentation === null ? null : undefined,
      video: video === null ? null : undefined,
    },
  );

  if (image || documentation || video) {
    const formData = new FormData();
    if (image) {
      formData.set('image', image);
    }
    if (documentation) {
      formData.set('documentation', documentation);
    }
    if (video) {
      formData.set('video', video);
    }

    const { data: project } = await requester().patch<Project>(
      `projects/${id}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          accept: 'application/json',
        },
      },
    );
    return project;
  }
  return projectWithoutFiles;
};

export interface GetProjectDataBody {
  url: string;
  tool: string;
}

export const getProjectData = async (body: GetProjectDataBody) => {
  const { data: projectMetadata } = await requester().post<ProjectMetadata>(
    `get-project-data/by-tool/`,
    body,
  );
  return projectMetadata;
};

export const upvoteProject = async (id: number) => {
  await requester().post(`projects/${id}/add-up-vote/`);
};

export const deleteProjectUpvote = async (id: number) => {
  await requester().post(`projects/${id}/remove-up-vote/`);
};
